/* =====================================================================
   SPAWNER — спавн врагов по кривой эскалации (CONFIG.phases).
   Спавнит за краем видимой области вокруг игрока. Боссы по таймеру.
   ===================================================================== */

import { CONFIG } from './config';
import { clamp, rand, chance, weightedPick, TAU } from './utils';
import { Game } from './game';
import { Audio2 } from './audio';

export const Spawner: any = {
  timer: 0,
  bossesSpawned: 0,
  nextBossT: 0,           // время следующего периодического босса (после bossTimes)
  _lastSteps: -1, _cachedPhase: null,   // кэш эскалированной фазы (см. effectivePhase)

  reset() { this.timer = 0; this.bossesSpawned = 0; this.nextBossT = 0; this._lastSteps = -1; this._cachedPhase = null; },

  currentPhase(t: number) {
    let ph = CONFIG.phases[0];
    for (const p of CONFIG.phases) if (t >= p.tStart) ph = p;
    return ph;
  },

  // эффективная фаза: после ПОСЛЕДНЕЙ табличной фазы статы врага и лимит плавно растут
  // от времени — бесконечная кривая «вечной ночи» (без новых типов врагов).
  effectivePhase(t: number) {
    const ph = this.currentPhase(t);
    const E = CONFIG.endless;
    const lastStart = CONFIG.phases[CONFIG.phases.length - 1].tStart;
    if (t <= lastStart) return ph;
    const steps = Math.floor((t - lastStart) / E.stepEvery);
    if (steps <= 0) return ph;
    // ступень меняется раз в stepEvery секунд — кэшируем, чтобы не аллоцировать объект
    // каждый кадр в hot-path вечной ночи
    if (steps === this._lastSteps && this._cachedPhase) return this._cachedPhase;
    this._lastSteps = steps;
    this._cachedPhase = {
      ...ph,
      hpMult:  ph.hpMult * (1 + E.hpStep * steps),
      spdMult: Math.min(E.spdCap, ph.spdMult + E.spdStep * steps),
      dmgMult: ph.dmgMult * (1 + E.dmgStep * steps),
      cap:     Math.min(E.capMax, ph.cap + E.capStep * steps),
    };
    return this._cachedPhase;
  },

  update(dt: number, t: number) {
    // боссы: сначала по таблице, затем периодически и бесконечно (только когда арена
    // свободна от предыдущего босса — чтобы не штабелировались)
    if (this.bossesSpawned < CONFIG.bossTimes.length && t >= CONFIG.bossTimes[this.bossesSpawned]) {
      this.spawnBoss(t);
      this.bossesSpawned++;
      this.nextBossT = t + CONFIG.endless.bossEvery;
    } else if (this.bossesSpawned >= CONFIG.bossTimes.length && !Game.bossActive && t >= this.nextBossT) {
      this.spawnBoss(t);
      this.bossesSpawned++;
      this.nextBossT = t + CONFIG.endless.bossEvery;
    }

    const ph = this.effectivePhase(t);
    // АРЕНА БОССА: пока босс жив — резко режем рой и темп волн, чтобы бой читался
    // (иначе босс приходит в полную толпу — главная причина «неубиваемости»)
    let cap = ph.cap, batch = ph.batch, interval = ph.spawnInterval;
    if (Game.bossActive) { cap = Math.floor(cap * 0.4); batch = Math.max(1, Math.floor(batch * 0.5)); interval *= 1.7; }
    // ТЕНЁТА (TD-захват): defend-стадия глушит обычный спавн (волны = весь спавн, чистая эскалация);
    // kindle-удержание — наоборот, рой гуще (давление розжига).
    if (Game.snareMode === 'defend' && Game.snareT > 0) return;
    if (Game.snareT > 0) { batch = Math.ceil(batch * CONFIG.snare.spawnBoost); interval /= CONFIG.snare.spawnBoost; }

    if (Game.enemies.active.length >= cap) return;
    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer += interval;

    const weights = this.effectiveWeights(ph);
    const n = Math.min(batch, cap - Game.enemies.active.length);
    for (let i = 0; i < n; i++) {
      const key = weightedPick(weights);
      this.spawnEnemy(key, ph);
    }
  },

  // веса фазы, помноженные на weightMul активных аномалий (Прилив роя, Голод…)
  effectiveWeights(ph: any) {
    const wm = Game.runMods && Game.runMods.weightMul;
    if (!wm) return ph.weights;
    let touched = false;
    const out: any = {};
    for (const k in ph.weights) {
      out[k] = ph.weights[k] * (wm[k] || 1);
      if (wm[k]) touched = true;
    }
    return touched ? out : ph.weights;
  },

  // позиция спавна вокруг игрока. КЛЮЧЕВОЕ для «давления»: спавним у КРОМКИ СВЕТА
  // («рвутся из теней» рядом), а не за краем экрана. На больших экранах свет —
  // островок в центре: спавн за экраном давал «пустоту, можно стоять» (жалоба плейтеста).
  // Привязка к свету гарантирует, что рой доходит и окружает. Но не дальше края экрана.
  // Против кайтинга: с долей bias спавним В СТОРОНУ движения (засада спереди).
  edgePos() {
    const cx = Game.player.x, cy = Game.player.y;
    const screenR = Math.hypot(Game.viewW / 2 + 60, Game.viewH / 2 + 60);
    // за кромкой света (в тьме) — близко достаточно, чтобы рой доходил и давил,
    // но не впритык (иначе спавн-кольцо душит — поймано регресс-харнесом)
    const spawnR = clamp(Game.player.light + rand(140, 280), 320, screenR + 20);
    const bias = clamp((Game.time - 60) / 120, 0, 0.65);
    const ld = Game.player.lastDir || { x: 0, y: 0 };
    let a: number;
    if ((ld.x || ld.y) && chance(bias)) a = Math.atan2(ld.y, ld.x) + rand(-0.9, 0.9);  // засада спереди ±~50°
    else a = rand(0, TAU);
    return { x: cx + Math.cos(a) * spawnR, y: cy + Math.sin(a) * spawnR };
  },

  spawnEnemy(key: string, ph: any) {
    const pos = this.edgePos();
    this.spawnTypeAt(key, pos.x, pos.y, ph, false);
  },

  // единая инициализация врага: фаза × модификаторы забега (глубина+аномалии).
  // Используется и волнами, и при разделении Дробителя (isChild=true).
  spawnTypeAt(key: string, x: number, y: number, ph: any, isChild: boolean) {
    const def = CONFIG.enemies[key];
    const rm = Game.runMods || { hp: 1, spd: 1, dmg: 1 };
    Game.enemies.spawn((e: any) => {
      e.typeKey = key;
      e.x = x; e.y = y;
      e.kx = 0; e.ky = 0;
      e.maxHp = def.hp * ph.hpMult * rm.hp;
      e.hp = e.maxHp;
      e.radius = def.radius;
      e.speed = def.speed * ph.spdMult * rm.spd;
      e.damage = def.damage * ph.dmgMult * rm.dmg;
      e.color = def.color;
      e.shape = def.shape;
      e.xp = def.xp;
      e.bigGem = !!def.bigGem;
      e.score = def.score;
      e.isBoss = false;
      e.flash = 0; e.hitCd = 0; e.dmgCd = 0; e.slowT = 0;
      e.wob = rand(0, TAU);
      e.isChild = !!isChild;
      // свет-механики
      e.drainLight = def.drainLight || 0;
      e.drainRange = def.drainRange || 0;
      e.split = (def.split && !isChild) ? def.split : null;   // осколки не делятся дальше
      e.splitCount = def.splitCount || 0;
      e.anchorOnDeath = !!def.anchorOnDeath;
      e.anchorRadius = def.anchorRadius || 0;
      e.anchorLife = def.anchorLife || 0;
      e.suppressLight = !!def.suppressLight;
      e.suppressRange = def.suppressRange || 0;
      e.ring = false; e.ringTimer = 0;
      e.bossKind = null; e.bossPhase = 0;
      // дальник: параметры стрельбы
      e.ranged = !!def.ranged;
      if (def.ranged) {
        e.shotDmg = def.shotDmg * ph.dmgMult * rm.dmg;
        e.shotSpeed = def.shotSpeed;
        e.shotCd = def.shotCd;
        e.shotRange = def.shotRange;
        e.shotRadius = def.shotRadius;
        e.shotTimer = def.shotCd * (0.5 + rand(0, 0.5));   // рассинхрон залпов
      }
      // HG-10 контр-враги
      e.heatImmune = !!def.heatImmune;
      e.lunge = !!def.lunge; e.lungeRange = def.lungeRange || 0; e.lungeSpeed = def.lungeSpeed || 0;
      e.lungeCd = def.lungeCd || 0; e.lungeTimer = def.lungeCd ? def.lungeCd * (0.5 + rand(0, 0.5)) : 0; e.lungeT = 0;
      e.kite = !!def.kite; e.kiteRange = def.kiteRange || 0;
      // слэм — только боссы; полный сброс полей на случай переиспользования объекта-Исполина из пула
      e.slam = false; e.slamTele = -1; e.slamCd = 0; e.slamR = 0; e.slamDmg = 0; e.slamTimer = 0;
    });
  },

  // ростер боссов по глубине: глубже — новые владыки тьмы. Циклится по idx —
  // в «вечной ночи» боссы идут бесконечно, перебирая весь ростер (разнообразие).
  bossKey(idx: number, depth: number) {
    let roster: string[];
    // Исполин (boss5) — климактический гигант: ставим в КОНЕЦ ростера, чтобы он приходил
    // позже и не вытеснял установленных владык (особ. Нулевую Точку boss4) из первых спавнов.
    if (depth >= 3) roster = ['boss3', 'boss4', 'boss', 'boss2', 'boss5'];
    else if (depth >= 1) roster = ['boss2', 'boss3', 'boss4', 'boss', 'boss5'];
    else roster = ['boss', 'boss2', 'boss3', 'boss4', 'boss5'];
    return roster[idx % roster.length];
  },

  // тест/чит: форс конкретного босса (visual.mjs --setup 'SPAWNER.spawnBossKey("boss5", GAME.time)')
  // не меняет bossesSpawned — плановый ростер не сдвигается (это чит-инструмент, не игровой поток)
  spawnBossKey(key: string, t: number) {
    this._forceBoss = key; this.spawnBoss(t); this._forceBoss = null;
  },

  spawnBoss(t: number) {
    const idx = this.bossesSpawned;
    const rm = Game.runMods || { hp: 1, dmg: 1, depth: 0 };
    const key = this._forceBoss || this.bossKey(idx, rm.depth || 0);
    const def = CONFIG.enemies[key];
    const pos = this.edgePos();
    // эскалация поздних боссов от длительности забега (вечная ночь крепчает)
    const E = CONFIG.endless;
    const lastStart = CONFIG.phases[CONFIG.phases.length - 1].tStart;
    const steps = Math.max(0, Math.floor((t - lastStart) / E.stepEvery));
    const bossScale = 1 + 0.10 * steps;
    // РАСЧИСТКА АРЕНЫ под бой с боссом (главный фикс «кучи врагов у босса»):
    // 1) отбросить ближний рой ударной волной, 2) проредить дальнюю тьму до арена-кэпа —
    // явление босса разгоняет мелкие тени (без дропа, просто растворяются во тьме).
    const arenaCap = Math.floor(this.effectivePhase(t).cap * 0.4);
    const mobs: any[] = [];
    for (const e of Game.enemies.active) {
      if (e.dead || e.isBoss) continue;
      const dx = e.x - Game.player.x, dy = e.y - Game.player.y;
      const d = Math.hypot(dx, dy) || 1;
      if (d < 360) { const f = 520 * (1 - d / 360); e.kx += dx / d * f; e.ky += dy / d * f; }
      mobs.push(e);
    }
    if (mobs.length > arenaCap) {
      const px = Game.player.x, py = Game.player.y;
      mobs.sort((a, b) => ((b.x - px) ** 2 + (b.y - py) ** 2) - ((a.x - px) ** 2 + (a.y - py) ** 2));
      for (let i = 0; i < mobs.length - arenaCap; i++) mobs[i].dead = true;   // дальние — прочь
    }
    Game.enemies.spawn((e: any) => {
      e.typeKey = key;
      e.x = pos.x; e.y = pos.y;
      e.kx = 0; e.ky = 0;
      e.maxHp = def.hp * rm.hp * bossScale;   // боссы крепнут с глубиной и длиной забега
      e.hp = e.maxHp;
      e.radius = def.radius;
      e.speed = def.speed;
      e.damage = def.damage * rm.dmg * bossScale;
      e.color = def.color;
      e.shape = def.shape;
      e.xp = def.xp;
      e.bigGem = true;
      e.score = def.score;
      e.isBoss = true;
      e.ranged = false;
      e.flash = 0; e.hitCd = 0; e.dmgCd = 0; e.slowT = 0; e.slowMul = 1;
      // свет-механики боссов (Владыка Затмения / Нулевая Точка)
      e.drainLight = def.drainLight || 0;
      e.drainRange = def.drainRange || 0;
      e.split = null; e.splitCount = 0;
      e.anchorOnDeath = !!def.anchorOnDeath;
      e.anchorRadius = def.anchorRadius || 0;
      e.anchorLife = def.anchorLife || 0;
      e.suppressLight = false; e.suppressRange = 0;
      e.ring = !!def.ring;
      e.ringCd = def.ringCd || 0; e.ringTimer = def.ringCd || 0;
      e.ringShots = def.ringShots || 0; e.ringSpeed = def.ringSpeed || 0;
      e.ringDmg = (def.ringDmg || 0) * rm.dmg; e.ringRadius = def.ringRadius || 6;
      // контр-флаги не для боссов (босс и так heatImmune через isBoss)
      e.heatImmune = false; e.lunge = false; e.kite = false;
      // Исполин: слэм по площади
      e.slam = !!def.slam; e.slamCd = def.slamCd || 0; e.slamTimer = (def.slamCd || 0) * 0.6;
      e.slamR = def.slamRadius || 0; e.slamDmg = (def.slamDmg || 0) * rm.dmg; e.slamTele = -1;
      e.phase2 = def.phase2 || null;   // вторая фаза (обретает новый примитив при низком HP)
      e.lunge = false; e.lungeT = 0;   // на случай phase2-lunge — стартуем без рывка
      e.bossKind = key; e.bossPhase = 0;
      e.wob = 0;
    });
    Game.bossActive = true;
    Game.banner('⚠ ' + def.name.toUpperCase() + ' ⚠', 2.4);
    Game.addShake(12);
    Audio2.boss();
    Audio2.musicSetMood('boss');
  },
};
